interface RGB {
  r: number
  g: number
  b: number
}

export function hexToRgb(hex: string): RGB {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

  if (!result) {
    throw new Error("Invalid hex string")
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  }
}

export function hexToCssRgba(hex: string, a: number) {
  let rgb = hexToRgb(hex)
  return `RGBA(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`
}

function luminanace(color: RGB) {
  var a = [color.r, color.g, color.b].map(function (v) {
      v /= 255
      return v <= 0.03928
          ? v / 12.92
          : Math.pow( (v + 0.055) / 1.055, 2.4 )
  })
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
}

function contrast(rgb1: RGB, rgb2: RGB) {
  var lum1 = luminanace(rgb1)
  var lum2 = luminanace(rgb2)
  var brightest = Math.max(lum1, lum2)
  var darkest = Math.min(lum1, lum2)
  return (brightest + 0.05)
       / (darkest + 0.05)
}

export function isBrighter(hexColor1: string, hexColor2: string): boolean {
  let rgb1 = hexToRgb(hexColor1)
  let rgb2 = hexToRgb(hexColor2)
  
  let luminance1 = luminanace(rgb1)
  let luminance2 = luminanace(rgb2)

  return luminance1 > luminance2
}

export function pickHigherContrastOption(option1: string, option2: string, constant: string) {
  let rgb1 = hexToRgb(option1)
  let rgb2 = hexToRgb(option2)
  let rgb3 = hexToRgb(constant)

  let contrast1 = contrast(rgb1, rgb3)
  let contrast2 = contrast(rgb2, rgb3)
  
  if (contrast1 > contrast2) {
    return option1
  }
  return option2
}

export function brightenHexColor(hexColor: string, amount: number) {
  let hexLettersOnly = hexColor.replace(/^#/, '')
  if (hexLettersOnly.length === 3) {
    hexLettersOnly = hexColor[0] + hexColor[0] + hexColor[1] + hexColor[1] + hexColor[2] + hexColor[2]
  }

  let rgb = hexToRgb("#" + hexLettersOnly)
  return rgbToHex(rgb.r + amount, rgb.g + amount, rgb.b + amount)
}

export function mixHexColors(baseColor: string, mixColor: string, percent: number): string {
  let baseRgb = hexToRgb(baseColor)
  let mixRgb = hexToRgb(mixColor)

  let r = (baseRgb.r * (1 - percent)) + (mixRgb.r * percent)
  let g = (baseRgb.g * (1 - percent)) + (mixRgb.g * percent)
  let b = (baseRgb.b * (1 - percent)) + (mixRgb.b * percent)

  return rgbToHex(Math.round(r), Math.round(g), Math.round(b))
}

function rgbToHex(r: number, g: number, b: number) {
  let rString = Math.max(Math.min(255, r), 0).toString(16)
  let gString = Math.max(Math.min(255, g), 0).toString(16)
  let bString = Math.max(Math.min(255, b), 0).toString(16)

  const rr = (rString.length < 2 ? '0' : '') + rString
  const gg = (gString.length < 2 ? '0' : '') + gString
  const bb = (bString.length < 2 ? '0' : '') + bString

  return `#${rr}${gg}${bb}`
}